import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useBookingActions } from '../../../../../hooks/booking/useBookingActions'
import { isUpcomingConfirmed } from '../../../../../hooks/booking/useBookingCancellation'

import Icon from '../../../../ui/Icon'
import { DropdownMenuItem } from '../../../../dropdownMenu/DropdownMenu'
import { RowActions } from '../../../../listItem/rowActions/RowActions'
import { ListRow } from '../../../../listItem/listRow/ListRow'
import { ListCard } from '../../../../listItem/listCard/ListCard'
import { ListingDetails } from '../../../../listing/ListingDetails'
import { BookingStatusBadge, bookingStatusStripe, isBookingVoided } from '../../../history/BookingStatusBadge'
import { BookingFundingBadge, useBookingFundingLabel } from '../../../history/BookingFundingBadge'

import { CONFIG } from '../../../../../config'
import { useApp } from '../../../../../contexts/AppContext'
import { useConfirm } from '../../../../../contexts/ConfirmContext'
import { DisplayType } from '../../../../../types/listing'
import { ConfirmDetailType, ConfirmLayout } from '../../../../../types/confirm/confirm'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import { Booking, BookingFilter } from '../../../../../types/booking/booking'
import { CancelReason } from '../../../../../types/court/court'
import { displayDate, toDisplayOnlyDate } from '../../../../../utils/dateUtils'
import { formatMinutes, timeOf } from '../../../../../utils/booking/booking'

type AllBookingListProps = {
  /** Null lists every day, latest start first (each row then shows its date). */
  selectedDay: Date | null
}

/**
 * Bookings of all customers on one day, or on every day. Cancelling here always returns value to the customer (prepaid minutes to their
 * passes, which are extended; what was paid as refund points).
 */
export const AllBookingList = ({ selectedDay }: AllBookingListProps) => {
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { t, i18n } = useTranslation(['bookings', 'history', 'exceptions'])
  const [refreshTrigger, setRefreshTrigger] = useState(false)
  const { cancelByManagement } = useBookingActions()
  const fundingLabel = useBookingFundingLabel()

  const handleCancelBooking = (item: Booking) => {
    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('history:history.cancel-confirmation.title'),
      subtitle: t('history:history.cancel-confirmation.subtitle'),
      confirmLabel: t('history:history.cancel-confirmation.confirm'),
      backLabel: t('bookings:booking-confirmation.back-label'),
      returnTo: '/m/all',
      itemCards: [
        {
          id: item.id,
          title: item.courtName,
          details: [
            {
              label: t('bookings:booking-confirmation.date-label'),
              value: toDisplayOnlyDate(item.startTime, i18n.language),
            },
            {
              label: t('bookings:booking-confirmation.start-time-label'),
              value: `${timeOf(item.startTime)}–${timeOf(item.endTime)}`,
            },
            {
              label: t('booking.all-booking-list.table-header.booked-by'),
              value: item.ownerName || item.ownerEmail || '—',
            },
            {
              key: 'cancelReason',
              label: t('history:history.cancel-confirmation.reason-label'),
              value: CancelReason.MAINTENANCE,
              type: ConfirmDetailType.SELECT,
              options: [CancelReason.MAINTENANCE, CancelReason.USER_REQUEST].map(reason => ({
                value: reason,
                label: t(`history:history.cancel-confirmation.reasons.${reason}`),
              })),
            },
          ],
        },
      ],
      warning: t('history:history.cancel-confirmation.management-note'),
      onConfirm: async (_, selectedValues) => {
        const reason = (selectedValues?.cancelReason as CancelReason | undefined) ?? CancelReason.MAINTENANCE
        await cancelByManagement(item.id, reason)
        setRefreshTrigger(value => !value)
        showBanner({ kind: BannerKind.OK, msg: t('bookings:successfully-canceled') })
      },
    })
  }

  const menuItems = (item: Booking): DropdownMenuItem[] =>
    isUpcomingConfirmed(item)
      ? [
          {
            key: 'cancel',
            label: t('booking.m.all-booking-row.cancel-btn'),
            icon: 'x',
            danger: true,
            onClick: () => handleCancelBooking(item),
          },
        ]
      : []

  const renderActions = (item: Booking) => (
    <RowActions label={t('history:history.booking-history-list.more-actions')} items={menuItems(item)} />
  )

  const renderBooking = (item: Booking, viewMode: DisplayType) => {
    const hours = `${timeOf(item.startTime)}–${timeOf(item.endTime)}`
    const time = selectedDay ? hours : `${toDisplayOnlyDate(item.startTime, i18n.language)} · ${hours}`
    const owner = item.ownerName || item.ownerEmail || '—'
    const creator = item.createdByName || owner
    const status = <BookingStatusBadge booking={item} />
    const funding = <BookingFundingBadge booking={item} />

    if (viewMode === DisplayType.LIST) {
      return (
        <ListRow
          key={item.id}
          cells={[
            { key: 'number', className: 'mono muted', content: `#${item.id}` },
            { key: 'time', className: 'mono fw-semibold', content: time },
            {
              key: 'court',
              content: (
                <div>
                  <div className="fw-semibold">{item.courtName}</div>
                  <div className="tiny muted">{formatMinutes(item.durationMinutes)}</div>
                </div>
              ),
            },
            { key: 'booked-by', content: owner },
            { key: 'created-by', content: creator },
            { key: 'funding', content: funding },
            { key: 'status', content: status },
          ]}
          actions={renderActions(item)}
        />
      )
    }

    return (
      <ListCard
        key={item.id}
        stripe={bookingStatusStripe(item)}
        title={item.courtName}
        meta={`#${item.id}`}
        subtitle={
          <>
            <Icon name="clock" size={12} />
            <span>{`${time} · ${formatMinutes(item.durationMinutes)}`}</span>
          </>
        }
        status={status}
        menu={renderActions(item)}
        muted={isBookingVoided(item)}
        fields={[
          {
            key: 'booked-by',
            label: t('booking.all-booking-list.table-header.booked-by'),
            value: owner,
            mono: false,
          },
          {
            key: 'created-by',
            label: t('booking.all-booking-list.table-header.created-by'),
            value: creator,
            mono: false,
          },
          {
            key: 'funding',
            label: t('booking.all-booking-list.table-header.payment-method'),
            value: fundingLabel(item),
            mono: false,
          },
        ]}
      />
    )
  }

  return (
    <ListingDetails<Booking>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/management/bookings`}
      refreshTrigger={refreshTrigger}
      initialViewMode={DisplayType.LIST}
      displaySearch
      searchPlaceholder={t('booking.all-booking-list.search-placeholder')}
      // Without a day the date is left out: every booking, latest first.
      additionalParams={{ ...(selectedDay && { date: displayDate(selectedDay) }), filter: BookingFilter.ALL }}
      tableColumns={[
        { key: 'number', label: t('booking.all-booking-list.table-header.number') },
        {
          key: 'time',
          label: t(selectedDay ? 'booking.all-booking-list.table-header.time' : 'booking.all-booking-list.table-header.date-time'),
        },
        { key: 'court', label: t('booking.all-booking-list.table-header.court') },
        { key: 'booked-by', label: t('booking.all-booking-list.table-header.booked-by') },
        { key: 'created-by', label: t('booking.all-booking-list.table-header.created-by') },
        { key: 'funding', label: t('booking.all-booking-list.table-header.payment-method') },
        { key: 'status', label: t('booking.all-booking-list.table-header.status') },
        { key: 'actions', label: t('booking.all-booking-list.table-header.actions'), align: 'end' },
      ]}
      renderItem={renderBooking}
    />
  )
}
